import { isReactNativeClient, runningClientSide } from './running';

export const productionBzNextHostnames = new Set([
  'www.benzinga.com',
  'kr.benzinga.com',
  'es.benzinga.com',
  'it.benzinga.com',
  'jp.benzinga.com',
  'fr.benzinga.com',
]);

const localBzNextHostnames = new Set([
  'localhost',
  '127.0.0.1',
  'local.benzinga.com',
  ...[...productionBzNextHostnames]
    .filter(productionHost => productionHost !== 'www.benzinga.com')
    .map(productionHost => `${productionHost.split('.')[0]}.local.benzinga.com`),
]);

// Pro (and some widgets) share localhost / local.benzinga.com with BZ Next.
// Treat those ports as not BZ so RestfulClient does not use BZ proxy paths.
//   9000  apps/pro Vite serve
//   4200  apps/pro-e2e Cypress (also widgets Vite defaults)
//   4300  apps/pro Vite preview
const localProPorts = new Set(['9000', '4200', '4300']);

export const getSubDomain = (hostname?: string): string => {
  if (!runningClientSide()) {
    return 'www';
  }

  const host = hostname ?? window.location.host;

  return host.split('.')[0];
};

export const isLocalBzDomain = (): boolean => {
  if (!runningClientSide()) return false;
  if (localProPorts.has(window.location.port)) return false;
  return localBzNextHostnames.has(window.location.hostname);
};

export const isProductionBzDomain = (): boolean => {
  if (!runningClientSide()) return false;
  return productionBzNextHostnames.has(window.location.hostname);
};

export const isSandboxBzDomain = (): boolean => {
  if (!runningClientSide()) return false;
  return window.location.hostname.includes('bzsbx.benzinga.com');
};

export const isStagingBzDomain = (): boolean => {
  if (!runningClientSide()) return false;
  const stagingSites = ['bz.zingbot.bz', 'wwwbeta.benzinga.com'];
  if (stagingSites.includes(window.location.hostname)) {
    return true;
  }
  return false;
};

export const isBetaBzDomain = (): boolean => {
  if (!runningClientSide()) return false;
  const site = 'wwwbeta.benzinga.com';
  const host = window.location.hostname;
  return host.includes(site);
};

export const isStagingOrSandboxBzDomain = (): boolean => {
  return isStagingBzDomain() || isSandboxBzDomain();
};

export const isBzDomain = (): boolean => {
  return isProductionBzDomain() || isStagingBzDomain() || isSandboxBzDomain() || isLocalBzDomain();
};

export const isSandboxProtoDomain = (): boolean => {
  if (!runningClientSide()) return false;
  const host = window.location.hostname;
  return host.includes('protosbx.benzinga.com') || host.includes('protosbx.zingbot.bz');
};

export const isStagingProtoDomain = (): boolean => {
  if (!runningClientSide()) return false;
  return window.location.hostname.includes('proto.benzinga.dev');
};

export const isProtoDomain = (): boolean => {
  if (!runningClientSide()) return false;
  const host = window.location.hostname;
  return (
    host.includes('widgets.benzinga.com') ||
    host.includes('protobeta.benzinga.com') ||
    isSandboxProtoDomain() ||
    isStagingProtoDomain()
  );
};

let forceProxyRoutes = false;

/** Opts a non-website client (bz-mobile) into the BFF proxy routes. Call before creating the session. */
export const setForceProxyRoutes = (enabled: boolean): void => {
  forceProxyRoutes = enabled;
};

export const isAbleToUseProxyRoutes = (): boolean => {
  if (forceProxyRoutes) return true;
  if (!runningClientSide() || !window.location || isReactNativeClient()) return false;
  return isBzDomain() || isProtoDomain();
};

// Pro production hosts. Only these serve the real Pro product to customers.
const proProductionHosts = new Set(['pro.benzinga.com', 'probeta.benzinga.com']);

export const isProProductionHost = (host: string): boolean => proProductionHosts.has(host);

// Lower-environment Pro deployments: staging and GitLab branch/preview sandboxes.
//   staging:  pro.zingbot.bz
//   preview:  <branch>-pro--<hash>.sbx.benzinga.com | .sbx.zingbot.bz
//             e.g. task-pro--abc.sbx.benzinga.com, feature-pro--abc.sbx.zingbot.bz,
//                  release-pro-v1-68-3.sbx.benzinga.com
// The leading-dot `.sbx.` suffix keeps this from matching the dot_com (`bzsbx`) or
// proto (`protosbx`) sandbox domains.
export const isProLowerEnvHost = (host: string): boolean => {
  if (host === 'pro.zingbot.bz') return true;
  const onProSandbox = host.endsWith('.sbx.benzinga.com') || host.endsWith('.sbx.zingbot.bz');
  if (!onProSandbox) return false;
  const label = host.split('.')[0] ?? '';
  return label.includes('pro');
};

export const isProHost = (host: string): boolean => isProProductionHost(host) || isProLowerEnvHost(host);

export const isProDomain = (): boolean => {
  if (!runningClientSide()) return false;
  return isProProductionHost(window.location.hostname);
};
